import {Inject, inject, Injectable, Injector, PLATFORM_ID} from '@angular/core';
import {
  AuthSession,
  autoSignIn,
  confirmResetPassword,
  fetchAuthSession,
  resetPassword,
  signIn,
  signOut,
  signUp,
  updatePassword
} from "aws-amplify/auth";
import {firstValueFrom} from "rxjs";
import {Router} from "@angular/router";
import {UserService} from "../user.service";
import {StorageService} from "../storage.service";
import {AnalyticsService} from "../analytics.service";
import {boroughsDto, roomPreferenceOptions, UpdateSignupFlowDataCommand} from "homey-housing-lottery-app-dto";
import {
  signupPaymentStartWithEmailRoute,
  signupPaymentStartWithPhoneRoute
} from "../../pages/signup-section/signup-route-values";
import {FederatedAuthPlatform, FederatedAuthService} from "./federated-auth.service";
import {UsernamePasswordAuthService} from "./username-password-auth.service";
import {isPlatformBrowser} from "@angular/common";
import {isNative} from "../../utils";
import {AuthService, AuthType, LoginResult, OAuthState, SignupResult, Tokens} from "./auth.service";
import {isInSignupFlow} from "../../model/user";

@Injectable({
  providedIn: 'root'
})
export class BrowserAuthService extends AuthService {
  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private readonly injector: Injector,
    private readonly router: Router,
    private readonly storageService: StorageService,
  ) {
    super();
  }

  private readonly federatedAuthService = inject(FederatedAuthService);
  private readonly usernamePasswordAuthService = inject(UsernamePasswordAuthService);

  private getAuthErrorReason(error: unknown): string {
    if (typeof error === 'object' && error !== null) {
      const message = 'message' in error && typeof error.message === 'string' ? error.message : undefined;
      const name = 'name' in error && typeof error.name === 'string' ? error.name : undefined;

      if (message?.includes("GoogleAccountException")) return 'google-account';
      if (message?.includes("AppleAccountException")) return 'apple-account';
      if (message?.includes("UserNotFoundException") || name === "UserNotFoundException") return 'invalid-email';
      if (message?.includes("NotAuthorizedException") || name === "NotAuthorizedException") return 'invalid-password';
      if (name === "UsernameExistsException") return 'username-exists';
    }

    return 'unknown';
  }

  public async getSpecificAuthService(authType: AuthType): Promise<FederatedAuthService | UsernamePasswordAuthService> {
    switch (authType) {
      case 'google':
        return this.federatedAuthService;
        case 'apple':
        return this.federatedAuthService;
      case 'username-password':
        return this.usernamePasswordAuthService;
    }
  }

  public async getLoginMethod(): Promise<'socials' | 'username-password' | undefined> {
    const type = await this.storageService.getItem('AUTH_TYPE');
    if (type === 'google') {
      return 'socials';
    } else if (type === 'apple') {
      return 'socials';
    } else if (type === 'username-password') {
      return 'username-password';
    } else {
      return undefined;
    }
  }

  private setHasAccountCookie(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.cookie = 'has-account=1; path=/; max-age=31536000; SameSite=Lax';
    }
  }

  public async retrieveData() {
    await this.onTokenChanged();
    void this.checkAndSetCookie();
  }

  private async checkAndSetCookie() {
    const tokens = await this.getTokens();
    if (tokens) {
      this.setHasAccountCookie();
    }
  }

  public async getTokens(): Promise<Tokens | undefined> {
    if (!isPlatformBrowser(this.platformId)) {
      return undefined;
    }

    let type = await this.storageService.getItem('AUTH_TYPE');
    let session: AuthSession | undefined;

    if (!type) {
      session = await fetchAuthSession();
      if (session?.tokens?.accessToken) {
        type = 'username-password';
        await this.storageService.setItem('AUTH_TYPE', type);

        if (!session.tokens) {
          console.warn('No tokens found in auth session, please login again');
          await this.storageService.removeItem('AUTH_TYPE');
          return undefined;
        }

        if (!session.tokens.idToken) {
          console.error('No id token found in auth session, please login again');
          await this.storageService.removeItem('AUTH_TYPE');
          return undefined;
        }

        if (!session.tokens.accessToken) {
          console.error('No access token found in auth session, please login again');
          await this.storageService.removeItem('AUTH_TYPE');
          return undefined;
        }

        return {
          source: 'username-password',
          idToken: session.tokens.idToken.toString(),
          accessToken: session.tokens.accessToken.toString(),
        }
      }
    }

    if (!type) {
      return undefined
    }

    const authService = await this.getSpecificAuthService(type);
    return await authService.getTokens();
  }

  public async forceRefresh(): Promise<Tokens | undefined> {
    if (!isPlatformBrowser(this.platformId)) return undefined;
    const type = await this.storageService.getItem('AUTH_TYPE');
    if (!type) return undefined;
    const service = await this.getSpecificAuthService(type);
    return service.forceRefresh();
  }

  private async onTokenChanged(): Promise<void> {
    await firstValueFrom(this.injector.get(UserService).retrieveUser());
  }

  public async socialLogin(platform: FederatedAuthPlatform, oathState: OAuthState): Promise<void> {
    await this.storageService.setItem('AUTH_TYPE', platform);
    const result = await this.federatedAuthService.federatedLogin(platform, oathState);

    if (result === 'cancelled') {
      console.warn("User cancelled the login flow");
      return;
    }

    this.setHasAccountCookie();
    await this.onTokenChanged();
    await this.loginCallBack(oathState);
  }

  async logout() {
    const authType = await this.storageService.getItem('AUTH_TYPE');
    await this.storageService.removeItem('AUTH_TYPE');

    if (!authType){
      await signOut();
    } else {
      const service = await this.getSpecificAuthService(authType);
      await service.logout();
    }

    await this.onTokenChanged();
    await this.router.navigate(['/']);
  }

  async signup(email: string, password: string, oauthState: OAuthState): Promise<SignupResult> {
    await signOut()
    const analyticsService = this.injector.get(
      AnalyticsService
    )
    let signUpNextStep: string | undefined;
    try {
      analyticsService.sendEvent(
        'signup-start'
      )
      const { nextStep } = await signUp({
        username: email,
        password: password,
        options: {
          userAttributes: { email },
          autoSignIn: true,
        },
      });
      signUpNextStep = nextStep.signUpStep;
    } catch (error) {
      const reason = this.getAuthErrorReason(error);
      analyticsService.sendEvent(
        'signup-failed',
        {reason}
      );
      if (reason === 'google-account') {
        return 'google-account';
      }
      if (reason === 'apple-account') {
        return 'apple-account';
      }
      if (reason === 'username-exists') {
        const loginResult = await this.homeyLogin(email, password, oauthState.redirectUrl ?? '/');

        switch (loginResult) {
          case 'success':
            analyticsService.sendEvent('login-after-signup')
            return 'success';
          case 'invalid-email':
            console.error("Invalid email during signup");
            throw new Error('Something went wrong during signup, please try again');
          case 'invalid-password':
            return 'invalid-password'
          case 'google-account':
            return 'google-account';
        }
      }
      throw error;
    }

    if (signUpNextStep === 'COMPLETE_AUTO_SIGN_IN') {
      await autoSignIn();
    } else {
      await signIn({
        username: email,
        password: password,
      });
    }

    await this.storageService.setItem('AUTH_TYPE', 'username-password');
    this.setHasAccountCookie();
    await this.onTokenChanged();

    analyticsService.sendEvent(
      'signup-success'
    );

    await this.loginCallBack(oauthState);
    return 'success'
  }

  public async changePassword(passwords: {
    oldPassword: string,
    newPassword: string,
  }) {
    await updatePassword({
      oldPassword: passwords.oldPassword,
      newPassword: passwords.newPassword,
    });
  }

  public async onFederatedLoginCallback(code: string) {
    if (isNative) {
      return;
    }
    const oathState = await this.federatedAuthService.handleWebCallback(code)
    this.setHasAccountCookie();
    await this.onTokenChanged();
    await this.loginCallBack(oathState ?? {
      redirectUrl: undefined,
      initialFormCommand: undefined,
    });
  }

  public async loginCallBack(authState: OAuthState) {
    const user = await firstValueFrom(this.injector.get(UserService).user$);

    if (isInSignupFlow(user)) {
      const analyticsService = this.injector.get(AnalyticsService);
      await analyticsService.sendEvent('signup');
      const utmData = analyticsService.getUTMData();
      const referrerUrl = typeof document !== 'undefined' ? document.referrer : 'unknown';
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
      const formCommand = authState.initialFormCommand ?? {
        householdIncome: '20000-50000',
        householdSize: 1,
        preferences: {
          borough: [...boroughsDto],
          room: [...roomPreferenceOptions],
        },
        referralCode: undefined,
        lotteryNotificationsEnabled: true,
        receivedRentalAssistance: false,
      };
      await firstValueFrom(this.injector.get(UserService).updateSignupFlowData({
        ...formCommand,
        metadata: {
          utmCampaign: utmData.utmCampaign,
          utmSource: utmData.utmSource,
          referrerUrl,
          userAgent,
        }
      }));

      if (user.email.includes('privaterelay')) {
        await this.router.navigate(signupPaymentStartWithEmailRoute, {replaceUrl: true});
      } else {
        await this.router.navigate(signupPaymentStartWithPhoneRoute, {replaceUrl: true});
      }
    } else {
      await this.router.navigateByUrl(authState.redirectUrl ?? '/', {replaceUrl: true});
    }
  }

  async homeyLogin(email: string, password: string, redirectUrl: string): Promise<LoginResult> {
    const analyticsService = this.injector.get(
      AnalyticsService
    );

    analyticsService.sendEvent(
      'login-start'
    );
    try {
      await signOut();
      await signIn({
        username: email,
        password: password,
        options: {
          authFlowType: 'USER_PASSWORD_AUTH',
        }
      });

      await this.storageService.setItem('AUTH_TYPE', 'username-password');
      this.setHasAccountCookie();
      await this.onTokenChanged();
      await this.loginCallBack({
        redirectUrl,
        initialFormCommand: undefined,
      });
      analyticsService.sendEvent(
        'login-success'
      );
      return 'success';
    } catch (error) {
      const reason = this.getAuthErrorReason(error);
      analyticsService.sendEvent(
        'login-failed',
        {reason}
      );
      switch (reason) {
        case 'invalid-email':
          return 'invalid-email';
        case 'invalid-password':
          return 'invalid-password';
        case 'google-account':
          return 'google-account';
        case 'apple-account':
          return 'apple-account';
        default:
          throw error;
      }
    }
  }

  async homeyForgotPassword(email: string) {
    const analyticsService = this.injector.get(
      AnalyticsService
    );

    void analyticsService.sendEvent(
      'password-reset-requested'
    );
    await resetPassword({
      username: email,
    })
  }

  async homeyConfirmResetPassword(email:
                                  string, code: string, newPassword: string
  ) {
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  }

  async onUnauthorized() {
    await this.logout();
    await this.onTokenChanged();
    await this.router.navigate(['/']);
  }
}
